import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PencilIcon, BookIcon, UsersIcon, CameraIcon, UserIcon, ImageIcon, XIcon, PaletteIcon, BrushIcon, FrameIcon, PenToolIcon, LayoutDashboardIcon, BookOpenIcon, ShoppingBagIcon, GiftIcon, BadgePercentIcon, GridIcon, FileTextIcon, MailIcon, ChevronRightIcon, ChevronDownIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
interface ServicesPopupProps {
  isOpen: boolean;
  onClose: () => void;
}
const ServicesPopup: React.FC<ServicesPopupProps> = ({
  isOpen,
  onClose
}) => {
  const dk = useDarkClasses();
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [expandedSubcategory, setExpandedSubcategory] = useState<string | null>(null);
  if (!isOpen) return null;
  const toggleCategory = (category: string) => {
    setExpandedCategory(expandedCategory === category ? null : category);
    setExpandedSubcategory(null); // Reset subcategory when changing category
  };
  const toggleSubcategory = (subcategory: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setExpandedSubcategory(expandedSubcategory === subcategory ? null : subcategory);
  };
  const serviceCategories = [{
    id: 'all-services',
    title: 'All Services',
    icon: <UsersIcon className="h-5 w-5 mr-3 text-purple-600" data-id="element-2803" data-mp="2n3dknmvnce8v" />,
    to: '/services/all',
    highlight: true
  }, {
    id: 'editing-services',
    title: 'Editing Services',
    icon: <PencilIcon className="h-5 w-5 mr-3 text-purple-500" data-id="element-2804" data-mp="1uim1w79cgwfy" />,
    items: [{
      label: 'View All Editors',
      icon: <UsersIcon className="h-5 w-5 mr-3 text-purple-500" data-id="element-2805" data-mp="24f8niy9hvrj1" />,
      to: '/services/editing/all'
    }, {
      label: 'Editor Gallery',
      icon: <ImageIcon className="h-5 w-5 mr-3 text-purple-500" data-id="element-2806" data-mp="1gnjluras9qpf" />,
      to: '/services/editing/gallery'
    }, {
      label: 'Browse Editors',
      icon: <PencilIcon className="h-5 w-5 mr-3 text-purple-500" data-id="element-2807" data-mp="3brrh267bsg2p" />,
      to: '/services/editing'
    }, {
      label: 'Connect with Editors',
      icon: <MailIcon className="h-5 w-5 mr-3 text-purple-500" data-id="element-2808" data-mp="3lhu7byn2bfxc" />,
      to: '/services/editing/connect'
    }, {
      label: 'Editing Opportunities',
      icon: <FileTextIcon className="h-5 w-5 mr-3 text-purple-500" data-id="element-2809" data-mp="3pls6uagausqx" />,
      to: '/services/editing/opportunities'
    }]
  }, {
    id: 'production-services',
    title: 'Production Services',
    icon: <BookIcon className="h-5 w-5 mr-3 text-purple-500" data-id="element-2810" data-mp="31umuw6dhcklw" />,
    items: [{
      label: 'Browse Production Pros',
      icon: <BookIcon className="h-5 w-5 mr-3 text-purple-500" data-id="element-2811" data-mp="36u98igx802v8" />,
      to: '/services/production'
    }, {
      label: 'Connect with Production',
      icon: <MailIcon className="h-5 w-5 mr-3 text-purple-500" data-id="element-2812" data-mp="m2fqxpacto3u" />,
      to: '/services/production/connect'
    }, {
      label: 'Production Opportunities',
      icon: <FileTextIcon className="h-5 w-5 mr-3 text-purple-500" data-id="element-2813" data-mp="r8ciiblrphc4" />,
      to: '/services/production/opportunities'
    }]
  }, {
    id: 'support-services',
    title: 'Support Services',
    icon: <UsersIcon className="h-5 w-5 mr-3 text-purple-500" data-id="element-2814" data-mp="igk9m5ce6g9n" />,
    items: [{
      label: 'Browse Support Pros',
      icon: <UsersIcon className="h-5 w-5 mr-3 text-purple-500" data-id="element-2815" data-mp="dhza684ys0m9" />,
      to: '/services/support'
    }, {
      label: 'Connect with Support',
      icon: <MailIcon className="h-5 w-5 mr-3 text-purple-500" data-id="element-2816" data-mp="3jjhi0qagjan" />,
      to: '/services/support/connect'
    }, {
      label: 'Support Opportunities',
      icon: <FileTextIcon className="h-5 w-5 mr-3 text-purple-500" data-id="element-2817" data-mp="1xw9beignpsi1" />,
      to: '/services/support/opportunities'
    }]
  }, {
    id: 'creative-arts',
    title: 'Creative Arts',
    icon: <PaletteIcon className="h-5 w-5 mr-3 text-purple-500" data-id="element-2818" data-mp="20xmbgf1hnrf1" />,
    items: [{
      id: 'all-creative',
      label: 'View All Creative Profiles',
      icon: <UsersIcon className="h-5 w-5 mr-3 text-purple-600" data-id="element-2819" data-mp="1rxg0azt9j3j1" />,
      to: '/creative/all',
      highlight: true
    }, {
      id: 'cover-design',
      label: 'Cover Design',
      icon: <PenToolIcon className="h-5 w-5 mr-3 text-purple-500" data-id="element-2820" data-mp="1i7webla96tjy" />,
      subitems: [{
        label: 'View All Cover Creatives',
        icon: <GridIcon className="h-5 w-5 mr-3 text-purple-600" data-id="element-2821" data-mp="1n0fsokgrlspt" />,
        to: '/creative/designers',
        highlight: true
      }, {
        label: 'Cover Gallery',
        icon: <ImageIcon className="h-5 w-5 mr-3 text-purple-500" data-id="element-2822" data-mp="1d670jxvnc1s6" />,
        to: '/creative/cover-gallery'
      }]
    }, {
      id: 'interior-art',
      label: 'Interior Book Art',
      icon: <LayoutDashboardIcon className="h-5 w-5 mr-3 text-purple-500" data-id="element-2823" data-mp="kn4ngxzze7cl" />,
      subitems: [{
        label: 'View All Interior Artists',
        icon: <GridIcon className="h-5 w-5 mr-3 text-purple-600" data-id="element-2824" data-mp="tn9kqfbgnpd1" />,
        to: '/creative/interior-artists',
        highlight: true
      }, {
        label: 'Interior Art Gallery',
        icon: <ImageIcon className="h-5 w-5 mr-3 text-purple-500" data-id="element-2825" data-mp="13jmlfx5fb3ha" />,
        to: '/creative/interior-art-gallery'
      }]
    }, {
      id: 'book-merch',
      label: 'Book Merch & Branding',
      icon: <ShoppingBagIcon className="h-5 w-5 mr-3 text-purple-500" data-id="element-2826" data-mp="1dcjxbkfwtu6x" />,
      subitems: [{
        label: 'View All Merch Creators',
        icon: <GridIcon className="h-5 w-5 mr-3 text-purple-600" data-id="element-2827" data-mp="1mbyluecinuos" />,
        to: '/creative/merch-creators',
        highlight: true
      }, {
        label: 'Merch Gallery',
        icon: <ImageIcon className="h-5 w-5 mr-3 text-purple-500" data-id="element-2828" data-mp="ewtjwcgoq2s6" />,
        to: '/creative/merch-gallery'
      }]
    }, {
      id: 'photography',
      label: 'Photography',
      icon: <CameraIcon className="h-5 w-5 mr-3 text-purple-500" data-id="element-2829" data-mp="sqb5e2sxlpku" />,
      subitems: [{
        label: 'View All Photography Pros',
        icon: <GridIcon className="h-5 w-5 mr-3 text-purple-600" data-id="element-2830" data-mp="xp5wds11ykg1" />,
        to: '/creative/photographers',
        highlight: true
      }, {
        label: 'Photography Gallery',
        icon: <ImageIcon className="h-5 w-5 mr-3 text-purple-500" data-id="element-2831" data-mp="17lr3qk0jo7n1" />,
        to: '/creative/photo-gallery'
      }, {
        label: 'Models',
        icon: <UserIcon className="h-5 w-5 mr-3 text-purple-500" data-id="element-2832" data-mp="39grm6yrkarc6" />,
        to: '/creative/models'
      }]
    }, {
      label: 'Submit Collaboration',
      icon: <UsersIcon className="h-5 w-5 mr-3 text-purple-500" data-id="element-2833" data-mp="35yc2a3c5cfi4" />,
      to: '/talent/collaborate'
    }]
  }];
  return <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-end justify-center md:hidden" data-id="element-2834" data-mp="2w6y6o6nbfzu9">
      <div className={`rounded-t-xl w-full max-h-[80vh] overflow-y-auto pb-safe ${dk.isDark ? 'bg-gray-800' : 'bg-white'}`} data-id="element-2835" data-mp="2s65y7fm9pyz1">
        <div className={`sticky top-0 z-10 px-4 py-3 border-b flex justify-between items-center ${dk.isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-2836" data-mp="3ovd8tslcmno6">
          <h2 className={`text-lg font-semibold ${dk.textPrimary}`} data-id="element-2837" data-mp="3ftvticfrmq0j">
            Services & Creative Arts
          </h2>
          <button onClick={onClose} className={`p-1 rounded-full ${dk.isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`} aria-label="Close menu" data-id="element-2838" data-mp="3avu9npaimmo0">
            <XIcon className={`h-6 w-6 ${dk.textMuted}`} data-id="element-2839" data-mp="1zfnkzlrtyh7w" />
          </button>
        </div>
        <div className="px-4 py-2" data-id="element-2840" data-mp="3c95wp75e5vc">
          {serviceCategories.map(category => <div key={category.id || category.title} className="mb-4" data-id="element-2841" data-mp="7g72as5392kp">
              {category.to ? <Link to={category.to} className={`flex items-center px-3 py-3 rounded-lg ${category.highlight ? dk.isDark ? 'bg-purple-900/30 text-purple-400 font-semibold' : 'bg-purple-50 text-purple-800 font-semibold' : dk.isDark ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-purple-50'}`} onClick={onClose} data-id="element-2842" data-mp="3pmleomnpl4gz">
                  {category.icon}
                  <span data-id="element-2843" data-mp="hdlj4suxbtdj">{category.title}</span>
                </Link> : <div data-id="element-2844" data-mp="hb8rfnzd64na">
                  <button className={`w-full flex items-center justify-between px-3 py-3 rounded-lg ${dk.isDark ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-purple-50'}`} onClick={() => toggleCategory(category.id || category.title)} data-id="element-2845" data-mp="153ft92nuz3u3">
                    <div className="flex items-center" data-id="element-2846" data-mp="2pkoymj0ydtad">
                      {category.icon}
                      <span className="font-medium" data-id="element-2847" data-mp="33c3lx21w8gn8">{category.title}</span>
                    </div>
                    <span data-id="element-2848" data-mp="2zbjnlv5l36zi">
                      {expandedCategory === (category.id || category.title) ? <ChevronDownIcon className={`h-5 w-5 ${dk.textMuted}`} data-id="element-2849" data-mp="2blpcbi0wxqhd" /> : <ChevronRightIcon className={`h-5 w-5 ${dk.textMuted}`} data-id="element-2850" data-mp="2kmdpkaihfmt7" />}
                    </span>
                  </button>
                  {expandedCategory === (category.id || category.title) && category.items && <div className="ml-4 mt-1 mb-2 space-y-1" data-id="element-2851" data-mp="3r1o3bgief5ue">
                        {category.items.map(item => <div key={item.id || item.label} data-id="element-2852" data-mp="1fuccxwikixm">
                            {item.subitems ? <div data-id="element-2853" data-mp="3ntaxwh4lvllj">
                                <button className={`w-full flex items-center justify-between px-3 py-2 rounded-lg ${dk.isDark ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-purple-50'}`} onClick={e => toggleSubcategory(item.id || item.label, e)} data-id="element-2854" data-mp="3nsj49bukjxxw">
                                  <div className="flex items-center" data-id="element-2855" data-mp="34uq94vq6ssb2">
                                    {item.icon}
                                    <span data-id="element-2856" data-mp="30v7q65hxerav">{item.label}</span>
                                  </div>
                                  <span data-id="element-2857" data-mp="130qzeespqj0h">
                                    {expandedSubcategory === (item.id || item.label) ? <ChevronDownIcon className={`h-4 w-4 ${dk.textMuted}`} data-id="element-2858" data-mp="2gylic2e72srv" /> : <ChevronRightIcon className={`h-4 w-4 ${dk.textMuted}`} data-id="element-2859" data-mp="2qr11v4553pev" />}
                                  </span>
                                </button>
                                {expandedSubcategory === (item.id || item.label) && <div className={`ml-4 mt-1 mb-2 space-y-1 border-l-2 pl-2 ${dk.isDark ? 'border-purple-800' : 'border-purple-100'}`} data-id="element-2860" data-mp="273a2thuyphj2">
                                    {item.subitems.map(subitem => <Link key={subitem.to} to={subitem.to} className={`flex items-center px-3 py-2 rounded-lg ${subitem.highlight ? dk.isDark ? 'bg-purple-900/30 text-purple-400 font-medium' : 'bg-purple-50 text-purple-800 font-medium' : dk.isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-purple-50'}`} onClick={onClose} data-id="element-2861" data-mp="3nh0b70t46ryi">
                                        {subitem.icon}
                                        <span data-id="element-2862" data-mp="1ebzl302cz5hl">{subitem.label}</span>
                                      </Link>)}
                                  </div>}
                              </div> : <Link to={item.to} className={`flex items-center px-3 py-2 rounded-lg ${item.highlight ? dk.isDark ? 'bg-purple-900/30 text-purple-400 font-medium' : 'bg-purple-50 text-purple-800 font-medium' : dk.isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-purple-50'}`} onClick={onClose} data-id="element-2863" data-mp="gwveyzbgls7n">
                                {item.icon}
                                <span data-id="element-2864" data-mp="3bayhixwk76lv">{item.label}</span>
                              </Link>}
                          </div>)}
                      </div>}
                </div>}
            </div>)}
        </div>
      </div>
    </div>;
};
export default ServicesPopup;